import React, { useEffect, useMemo, useState } from "react";
import { Col, Row } from "reactstrap";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import AppModal from "../../Common/AppModal";

import {
  createSecurityAuthMissingRequest,
  createSecurityAuthMissingRequestSuccess,
  getMasterSecurityAuthCapabilityList,
} from "../../../store/actions";

/**
 * Request Missing Permission
 *
 * Lets a user ask their company owner to grant one or more security
 * capabilities they do not currently hold. Backing endpoint is fire-and-forget:
 * it records the request(s) and emails the company owner (see
 * SecurityAuthMissingRequestController). Only the `create` route exists, so
 * there is no list/history/approval UI here.
 *
 * The `create` endpoint takes an array, so each ticked capability becomes its
 * own request row (and its own owner email) sharing the same reason.
 *
 * `preSelectedCapabilityId` accepts a single id or an array of ids to pre-tick
 * when you already know which capability the user was blocked on (e.g. from a
 * permission-denied prompt).
 */
export default function RequestMissingPermission({
  modal,
  setModal,
  toggle,
  preSelectedCapabilityId = null,
}) {
  const dispatch = useDispatch();

  // Selection is a Set of string ids for O(1) toggle/lookup.
  const [selectedIds, setSelectedIds] = useState(() => {
    const seed = Array.isArray(preSelectedCapabilityId)
      ? preSelectedCapabilityId
      : preSelectedCapabilityId != null
      ? [preSelectedCapabilityId]
      : [];
    return new Set(seed.map(String));
  });
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");

  const {
    masterSecurityAuthCapabilityListAPI,
    securityAuthMissingRequestCreateSuccessAPI,
    securityAuthMissingRequestCreateLoadingAPI,
  } = useSelector((state) => ({
    masterSecurityAuthCapabilityListAPI:
      state.Security.masterSecurityAuthCapabilityList
        ?.mastersecurityauthcapability_list,
    securityAuthMissingRequestCreateSuccessAPI:
      state.Security.securityAuthMissingRequestCreateSuccess,
    securityAuthMissingRequestCreateLoadingAPI:
      state.Security.securityAuthMissingRequestCreateLoading,
  }));

  // Load the capability catalogue so we can render the picker.
  useEffect(() => {
    dispatch(getMasterSecurityAuthCapabilityList());
  }, []);

  const capabilities = useMemo(
    () => masterSecurityAuthCapabilityListAPI ?? [],
    [masterSecurityAuthCapabilityListAPI]
  );

  // Filter by search over the capability name and id.
  const filteredCapabilities = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return capabilities;
    return capabilities.filter((item) => {
      const name = item?.Name?.toString()?.toLowerCase() ?? "";
      const id = item?.Id?.toString() ?? "";
      return name.includes(term) || id.includes(term);
    });
  }, [capabilities, search]);

  // Resolve the ticked ids back to full rows for the "selected" chips, so the
  // user can still see and remove picks that the current search filters out.
  const selectedItems = useMemo(
    () => capabilities.filter((item) => selectedIds.has(String(item?.Id))),
    [capabilities, selectedIds]
  );

  const toggleId = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const clearAll = () => setSelectedIds(new Set());

  const handleSubmit = () => {
    if (selectedIds.size === 0) {
      toast.error("Please tick at least one permission you need.");
      return;
    }
    if (isEmpty(message.trim())) {
      toast.error("Please add a short reason for your request.");
      return;
    }
    const reason = message.trim();
    dispatch(
      createSecurityAuthMissingRequest(
        Array.from(selectedIds).map((id) => ({
          MasterSecurityAuthCapability_Id: Number(id),
          Message: reason,
        }))
      )
    );
  };

  useEffect(() => {
    if (isEmpty(securityAuthMissingRequestCreateSuccessAPI)) return;
    if (securityAuthMissingRequestCreateSuccessAPI?.status === 200) {
      toast.success(
        selectedIds.size > 1
          ? "Requests sent to your account owner."
          : "Request sent to your account owner."
      );
      setMessage("");
      setSearch("");
      if (!preSelectedCapabilityId) setSelectedIds(new Set());
      setModal(false);
    } else {
      toast.error(
        securityAuthMissingRequestCreateSuccessAPI?.message ??
          "We could not send your request. Please try again."
      );
    }
    dispatch(createSecurityAuthMissingRequestSuccess([]));
  }, [securityAuthMissingRequestCreateSuccessAPI]);

  return (
    <AppModal
      open={modal}
      onClose={toggle}
      title="Request Permissions"
      subtitle="Tick the permissions you need. We will email your account owner to ask them to grant this access."
      icon={<LockOpenOutlinedIcon />}
      headerAccent="indigo"
      size="md"
      loading={securityAuthMissingRequestCreateLoadingAPI}
      submitLabel={
        selectedIds.size > 1
          ? `Send Request (${selectedIds.size})`
          : "Send Request"
      }
      submitColor="success"
      submitDisabled={selectedIds.size === 0 || isEmpty(message.trim())}
      onSubmit={handleSubmit}
    >
      <Row className="g-3">
        <Col lg={12}>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <label className="form-label mb-0">Permissions needed</label>
            {selectedIds.size > 0 && (
              <span className="d-flex align-items-center gap-2">
                <span className="badge badge-soft-primary">
                  {selectedIds.size} selected
                </span>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-muted"
                  onClick={clearAll}
                >
                  Clear
                </button>
              </span>
            )}
          </div>

          {/* Selected chips (survive filtering) */}
          {selectedItems.length > 0 && (
            <div className="d-flex flex-wrap gap-1 mb-2">
              {selectedItems.map((item) => (
                <span
                  key={item?.Id}
                  className="badge badge-soft-info d-flex align-items-center gap-1"
                >
                  {item?.Name}
                  <i
                    className="ri-close-line cursor-pointer"
                    onClick={() => toggleId(item?.Id)}
                  ></i>
                </span>
              ))}
            </div>
          )}

          {/* Search */}
          <div className="search-box mb-2">
            <input
              type="search"
              className="form-control"
              placeholder="Search permissions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Tickable list */}
          <div
            className="border rounded"
            style={{ maxHeight: "280px", overflowY: "auto" }}
          >
            {isEmpty(filteredCapabilities) ? (
              <div className="text-muted text-center p-3">
                {isEmpty(capabilities)
                  ? "Loading permissions..."
                  : "No permissions match your search."}
              </div>
            ) : (
              filteredCapabilities.map((item) => {
                const key = String(item?.Id);
                const checked = selectedIds.has(key);
                return (
                  <label
                    key={item?.Id}
                    className="d-flex align-items-center gap-2 px-2 py-1 m-0 cursor-pointer permission-request-row"
                    style={{ borderBottom: "1px solid var(--vz-border-color)" }}
                  >
                    <input
                      type="checkbox"
                      className="form-check-input m-0"
                      checked={checked}
                      onChange={() => toggleId(item?.Id)}
                    />
                    <span className="flex-grow-1">{item?.Name}</span>
                  </label>
                );
              })
            )}
          </div>
        </Col>

        <Col lg={12}>
          <label className="form-label">Why do you need this?</label>
          <textarea
            className="form-control"
            rows="3"
            placeholder="e.g. I need to raise credit notes for customer refunds"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          ></textarea>
        </Col>
      </Row>
    </AppModal>
  );
}
